/**
 * ContractListKpiCards - Cards de KPI totalizadores para listagem de contratos gerados
 * 
 * Exibe 5 métricas: Total, Criados Hoje, Enviados, Aguardando Assinatura, Assinados
 * Cards clicáveis que filtram a listagem por status (toggle)
 * Cores seguem o mesmo padrão visual do TaskListKpiCards
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-09 v1.0.0 - Criação inicial
 */

import { ListChecks, CalendarPlus, FileCheck, PauseCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ContractListKpis } from '@/hooks/useContractListKpis';

/** Props do componente */
interface ContractListKpiCardsProps {
  /** Dados dos KPIs */
  kpis: ContractListKpis;
  /** Se está carregando */
  loading?: boolean;
  /** Status atualmente filtrado (para highlight do card ativo) */
  activeStatus?: string;
  /** Callback ao clicar em um card para filtrar por status */
  onFilterByStatus: (status: string | null) => void;
}

/** Definição de cada card KPI */
interface KpiCardDef {
  /** Chave do status para filtro (null = total, sem filtro direto) */
  statusKey: string | null;
  /** Título exibido */
  title: string;
  /** Campo do KPI */
  field: keyof ContractListKpis;
  /** Ícone Lucide */
  icon: typeof ListChecks;
  /** Cor do ícone (mantém cor semântica) */
  iconColor: string;
  /** Cor de fundo do card inteiro */
  cardBg: string;
  /** Cor do ring quando ativo */
  ringColor: string;
}

/** Configuração dos 5 cards conforme paleta de referência (mesma do TaskListKpiCards) */
const KPI_CARDS: KpiCardDef[] = [
  {
    statusKey: null,
    title: 'Total de Contratos',
    field: 'total_contracts',
    icon: ListChecks,
    iconColor: 'text-purple-700 dark:text-purple-300',
    cardBg: 'bg-purple-100 dark:bg-purple-900/30',
    ringColor: 'ring-purple-400',
  },
  {
    statusKey: 'created_today',
    title: 'Criados Hoje',
    field: 'created_today',
    icon: CalendarPlus,
    iconColor: 'text-cyan-700 dark:text-cyan-300',
    cardBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    ringColor: 'ring-cyan-400',
  },
  {
    statusKey: 'generated',
    title: 'PDF Gerado',
    field: 'pdf_generated',
    icon: FileCheck,
    iconColor: 'text-blue-700 dark:text-blue-300',
    cardBg: 'bg-blue-100 dark:bg-blue-900/30',
    ringColor: 'ring-blue-400',
  },
  {
    statusKey: 'pending_signature',
    title: 'Aguardando Assinatura',
    field: 'pending_signature',
    icon: PauseCircle,
    iconColor: 'text-pink-700 dark:text-pink-300',
    cardBg: 'bg-pink-100 dark:bg-pink-900/30',
    ringColor: 'ring-pink-400',
  },
  {
    statusKey: 'signed',
    title: 'Assinados',
    field: 'signed',
    icon: CheckCircle2,
    iconColor: 'text-green-700 dark:text-green-300',
    cardBg: 'bg-lime-100 dark:bg-green-900/30',
    ringColor: 'ring-green-400',
  },
];

export function ContractListKpiCards({ kpis, loading, activeStatus, onFilterByStatus }: ContractListKpiCardsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 px-4 py-3">
      {KPI_CARDS.map((card) => {
        /** Verifica se este card está ativo (filtro selecionado) */
        const isActive = activeStatus === card.statusKey;

        return (
          <Card
            key={card.field}
            className={cn(
              'border-0 shadow-none cursor-pointer transition-all hover:shadow-md',
              card.cardBg,
              isActive && `ring-2 ${card.ringColor} shadow-md`
            )}
            onClick={() => {
              // Toggle: se já está ativo, limpa o filtro
              if (isActive) {
                onFilterByStatus(null);
              } else {
                onFilterByStatus(card.statusKey);
              }
            }}
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                {/* Ícone + título */}
                <div className="flex items-center gap-2 min-w-0">
                  <card.icon className={`h-5 w-5 shrink-0 ${card.iconColor}`} />
                  <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">
                    {card.title}
                  </p>
                </div>
                {/* Valor */}
                {loading ? (
                  <div className="h-6 w-8 bg-white/40 dark:bg-white/10 animate-pulse rounded" />
                ) : (
                  <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
                    {kpis[card.field]}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
